import { useState, useCallback } from 'react';
import { Check, Calculator, FlaskConical, Droplet } from 'lucide-react';
import { useExperiment } from '@/context';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Calculating EDTA Molarity',
    content: 'The EDTA molarity is determined from standardization: M_EDTA = (V_Mg x M_Mg) / V_EDTA. Since EDTA forms 1:1 complexes with both Ca2+ and Mg2+, moles of metal = moles of EDTA at the endpoint. This standardized concentration will be used for all sample calculations.'
  },
  2: {
    title: 'Calcium ppm Calculation',
    content: 'Calculate ppm Ca2+: (1) moles EDTA = Titre EDTA (L) x Molarity EDTA. (2) moles EDTA = moles Ca2+ (1:1 stoichiometry). (3) ppm Ca2+ = (moles Ca2+ x Molar Mass Ca2+ x 10^6) / 25 g. Your sample size was 25.00 mL; assume density = 1.0 g/mL. Remember the sample was diluted 5-fold, so account for this in the final result.'
  },
  3: {
    title: 'Lab Questions',
    content: '1. What is the expected amount of Ca2+ in water from the Durham region? 2. How do your results compare with expected quantities? 3. Calculate the expected Molarity of the EDTA solution prepared in Part I (remember disodium salt was used). 4. Compare the Molarity from standardization with the calculated value (% difference). 5. How would you make 100 mL of 0.03 M HCl from a 3 M HCl stock solution?'
  }
};

interface HardnessClassification {
  category: string;
  color: string;
  description: string;
}

function getHardnessClassification(ppm: number): HardnessClassification {
  if (ppm < 60) {
    return { category: 'Soft', color: '#22C55E', description: 'Low mineral content, ideal for most uses' };
  } else if (ppm < 120) {
    return { category: 'Moderately Hard', color: '#F59E0B', description: 'Acceptable for most purposes' };
  } else if (ppm < 180) {
    return { category: 'Hard', color: '#EF4444', description: 'May cause scale buildup' };
  } else {
    return { category: 'Very Hard', color: '#DC2626', description: 'Significant mineral content' };
  }
}

export default function Part4Calculations() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();

  // Get data from previous parts
  const standardizationData = state?.experimentData?.standardization?.titrationData || [];
  const sampleResult = state?.experimentData?.sampleAnalysis?.sampleResult || { volumeEDTA: null };

  const partState = state?.experimentData?.calculations || {
    calculatedMolarity: null,
    calculatedPPM: null,
    hardnessClassification: null
  };

  const [showFormula, setShowFormula] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('calculations', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Calculate average EDTA volume from standardization
  const validTrials = standardizationData.filter((d: { volumeEDTA: number | null }) => d.volumeEDTA !== null);
  const avgEDTAVolume = validTrials.length > 0
    ? validTrials.reduce((sum: number, d: { volumeEDTA: number }) => sum + d.volumeEDTA, 0) / validTrials.length
    : 20.5; // Default value for demo

  // Known Mg standard concentration (typical)
  const mgStandardMolarity = 0.01; // 0.01 M Mg standard
  const mgStandardVolume = 25.0; // mL

  const handleCalculateMolarity = useCallback(() => {
    // EDTA Molarity = (V_Mg * M_Mg) / V_EDTA
    const edtaMolarity = (mgStandardVolume * mgStandardMolarity) / avgEDTAVolume;
    updateState({ calculatedMolarity: edtaMolarity });
    completeStep('calculations-1');
  }, [avgEDTAVolume, updateState, completeStep]);

  const handleCalculatePPM = useCallback(() => {
    if (partState.calculatedMolarity) {
      const sampleVolume = sampleResult.volumeEDTA || 22.5; // mL EDTA used
      const sampleAliquot = 25.0; // mL sample

      // moles Ca = M_EDTA * V_EDTA (in L)
      const molesCa = partState.calculatedMolarity * (sampleVolume / 1000);

      // mass Ca = moles * molar mass (40.08 g/mol)
      const massCa = molesCa * 40.08;

      // ppm = (mass in g / volume in L) * 1000 = mg/L
      const ppmCa = (massCa / (sampleAliquot / 1000)) * 1000;

      // Convert to CaCO3 equivalent (ppm as CaCO3)
      const ppmAsCaCO3 = ppmCa * (100.09 / 40.08);

      updateState({ calculatedPPM: ppmAsCaCO3 });
      completeStep('calculations-2');
    }
  }, [partState.calculatedMolarity, sampleResult.volumeEDTA, updateState, completeStep]);

  const handleClassifyHardness = useCallback(() => {
    if (partState.calculatedPPM) {
      const classification = getHardnessClassification(partState.calculatedPPM);
      updateState({ hardnessClassification: classification });
      completeStep('calculations-3');
    }
  }, [partState.calculatedPPM, updateState, completeStep]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Calculator size={16} />
          <h3>Part IV: Calculations</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Calculate EDTA Molarity', hint: 'From standardization data' },
            { id: 2, title: 'Calculate Calcium ppm', hint: 'Determine calcium content' },
            { id: 3, title: 'Classify Water Hardness', hint: 'Compare to hardness scale' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`calculations-${step.id}`) ? 'completed' : ''}`}
            >
              <div className={`step-number ${isStepCompleted(`calculations-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`calculations-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                <span className="step-hint">{step.hint}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Data Summary */}
        <div className="data-summary">
          <h4>Experimental Data</h4>
          <div className="data-row">
            <span>Avg EDTA Volume:</span>
            <span>{avgEDTAVolume.toFixed(2)} mL</span>
          </div>
          <div className="data-row">
            <span>Mg Standard:</span>
            <span>{mgStandardMolarity} M</span>
          </div>
          <div className="data-row">
            <span>Sample EDTA Used:</span>
            <span>{(sampleResult.volumeEDTA || 22.5).toFixed(2)} mL</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Calculations"
        completedCount={state?.completedSteps.filter(s => s.startsWith('calculations-')).length || 0}
        totalSteps={3}
        equipment={[]}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="calculations-workspace">
          {/* Step 1: EDTA Molarity */}
          <div className="calculation-card">
            <div className="calc-header">
              <FlaskConical size={24} />
              <h3>Step 1: Calculate EDTA Molarity</h3>
            </div>

            <div className="formula-box">
              <p className="formula">
                M<sub>EDTA</sub> = (V<sub>Mg</sub> × M<sub>Mg</sub>) / V<sub>EDTA</sub>
              </p>
              <p className="formula-values">
                = ({mgStandardVolume} mL × {mgStandardMolarity} M) / {avgEDTAVolume.toFixed(2)} mL
              </p>
            </div>

            {partState.calculatedMolarity ? (
              <div className="result-box success">
                <Check size={20} />
                <span>EDTA Molarity = <strong>{partState.calculatedMolarity.toFixed(5)} M</strong></span>
              </div>
            ) : (
              <button
                className="calc-btn"
                onClick={handleCalculateMolarity}
              >
                <Calculator size={18} />
                Calculate Molarity
              </button>
            )}
          </div>

          {/* Step 2: Calcium PPM */}
          <div className={`calculation-card ${!partState.calculatedMolarity ? 'disabled' : ''}`}>
            <div className="calc-header">
              <Droplet size={24} />
              <h3>Step 2: Calculate Calcium Content</h3>
            </div>

            <div className="formula-box">
              <p className="formula">
                ppm CaCO<sub>3</sub> = (M<sub>EDTA</sub> × V<sub>EDTA</sub> × 100.09 × 1000) / V<sub>sample</sub>
              </p>
              {partState.calculatedMolarity && (
                <p className="formula-values">
                  = ({partState.calculatedMolarity.toFixed(5)} × {(sampleResult.volumeEDTA || 22.5).toFixed(2)} × 100.09 × 1000) / 25.0
                </p>
              )}
            </div>

            {partState.calculatedPPM ? (
              <div className="result-box success">
                <Check size={20} />
                <span>Water Hardness = <strong>{partState.calculatedPPM.toFixed(1)} ppm as CaCO<sub>3</sub></strong></span>
              </div>
            ) : (
              <button
                className="calc-btn"
                onClick={handleCalculatePPM}
                disabled={!partState.calculatedMolarity}
              >
                <Calculator size={18} />
                Calculate PPM
              </button>
            )}
          </div>

          {/* Step 3: Hardness Classification */}
          <div className={`calculation-card ${!partState.calculatedPPM ? 'disabled' : ''}`}>
            <div className="calc-header">
              <h3>Step 3: Water Hardness Classification</h3>
            </div>

            <div className="hardness-scale">
              <div className="scale-item soft">
                <span className="range">0 - 60</span>
                <span className="label">Soft</span>
              </div>
              <div className="scale-item moderate">
                <span className="range">60 - 120</span>
                <span className="label">Moderate</span>
              </div>
              <div className="scale-item hard">
                <span className="range">120 - 180</span>
                <span className="label">Hard</span>
              </div>
              <div className="scale-item very-hard">
                <span className="range">&gt; 180</span>
                <span className="label">Very Hard</span>
              </div>
            </div>

            {partState.hardnessClassification ? (
              <div
                className="result-box classification"
                style={{ borderColor: partState.hardnessClassification.color }}
              >
                <div
                  className="classification-badge"
                  style={{ backgroundColor: partState.hardnessClassification.color }}
                >
                  {partState.hardnessClassification.category}
                </div>
                <p>{partState.hardnessClassification.description}</p>
                <p className="ppm-value">
                  Your sample: <strong>{partState.calculatedPPM?.toFixed(1)} ppm</strong>
                </p>
              </div>
            ) : (
              <button
                className="calc-btn"
                onClick={handleClassifyHardness}
                disabled={!partState.calculatedPPM}
              >
                <Check size={18} />
                Classify Hardness
              </button>
            )}
          </div>

          {/* Completion Message */}
          {isStepCompleted('calculations-3') && (
            <div className="completion-banner">
              <Check size={32} />
              <div>
                <h3>Experiment Complete!</h3>
                <p>You have successfully analyzed the water sample using EDTA complexometric titration.</p>
              </div>
            </div>
          )}
        </div>
      </LabBench>

      <style>{`
        .calculations-workspace {
          display: flex;
          flex-direction: column;
          gap: 20px;
          padding: 20px;
          width: 100%;
          max-width: 600px;
        }

        .calculation-card {
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          transition: opacity 0.3s;
        }

        .calculation-card.disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        .calc-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .calc-header h3 {
          font-size: 16px;
          margin: 0;
          color: var(--text-primary);
        }

        .calc-header svg {
          color: var(--primary);
        }

        .formula-box {
          background: var(--bg-dark);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .formula {
          font-size: 16px;
          font-family: 'Times New Roman', serif;
          color: var(--text-primary);
          margin: 0 0 8px 0;
          text-align: center;
        }

        .formula-values {
          font-size: 14px;
          font-family: 'Courier New', monospace;
          color: var(--text-secondary);
          margin: 0;
          text-align: center;
        }

        .result-box {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          border-radius: 8px;
          font-size: 15px;
        }

        .result-box.success {
          background: rgba(0, 168, 107, 0.15);
          color: var(--secondary);
        }

        .result-box.classification {
          flex-direction: column;
          align-items: flex-start;
          border: 2px solid;
          background: var(--bg-elevated);
        }

        .classification-badge {
          padding: 6px 16px;
          border-radius: 20px;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }

        .result-box.classification p {
          margin: 8px 0 0 0;
          color: var(--text-secondary);
          font-size: 13px;
        }

        .ppm-value {
          color: var(--text-primary) !important;
          font-size: 14px !important;
        }

        .calc-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .calc-btn:hover:not(:disabled) {
          background: var(--primary-dark);
          transform: translateY(-2px);
        }

        .calc-btn:disabled {
          background: var(--border);
          cursor: not-allowed;
        }

        .hardness-scale {
          display: flex;
          gap: 4px;
          margin-bottom: 16px;
          border-radius: 8px;
          overflow: hidden;
        }

        .scale-item {
          flex: 1;
          padding: 12px 8px;
          text-align: center;
          color: white;
          font-size: 11px;
        }

        .scale-item .range {
          display: block;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .scale-item .label {
          font-size: 10px;
          opacity: 0.9;
        }

        .scale-item.soft { background: #22C55E; }
        .scale-item.moderate { background: #F59E0B; }
        .scale-item.hard { background: #EF4444; }
        .scale-item.very-hard { background: #DC2626; }

        .data-summary {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .data-summary h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 12px 0;
          text-transform: uppercase;
        }

        .data-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 4px 0;
          border-bottom: 1px solid var(--border);
        }

        .data-row:last-child {
          border-bottom: none;
        }

        .data-row span:first-child {
          color: var(--text-secondary);
        }

        .data-row span:last-child {
          color: var(--text-primary);
          font-family: 'Courier New', monospace;
        }

        .completion-banner {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          border-radius: 12px;
          color: white;
        }

        .completion-banner h3 {
          margin: 0 0 4px 0;
          font-size: 18px;
        }

        .completion-banner p {
          margin: 0;
          opacity: 0.9;
          font-size: 14px;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp4"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
